import React from 'react';
import HelpBadge from './components/HelpBadge';
import { isRealGatewayAccessToken } from './gatewayAuth';
import { isRoleAccessDeniedMessage } from './subscriberNavAccess';
import {
  planFromTierName,
  RAZZMATAZZ_WORKFLOW,
  type RazzmatazzCapability,
  type RazzmatazzStep,
  stepIncludedInPlan,
  stepsForCapability,
} from './razzmatazzWorkflow';
import MarketingStudioPage from './MarketingStudioPage';
import RazzmatazzTrafficPanel from './RazzmatazzTrafficPanel';
import RazzmatazzNotifyPanel from './RazzmatazzNotifyPanel';
import RazzmatazzSocialIntakePanel from './RazzmatazzSocialIntakePanel';
import RazzmatazzBookingPanel from './RazzmatazzBookingPanel';
import RazzmatazzPipelinePanel from './RazzmatazzPipelinePanel';
import AssentWorkspace from './AssentWorkspace';
import './components/ComplianceConsolePanel.css';
import './RazzmatazzWorkspace.css';


type RazzmatazzWorkspaceProps = {
  /** When embedding from a host shell, prefer this project name (e.g. GossLed). */
  preferredProjectName?: string;
  /**
   * Scope Traffic analytics to this marketing host (e.g. jfgoss.com for GossLed embed).
   * Empty = all hosts on the gateway store (NextCompli console default).
   */
  trafficHost?: string;
  /**
   * `minimal` hides the plan chip + project picker until the operator expands
   * Studio setup (NextCompli console — traffic does not need a project).
   * `full` keeps the picker always visible (multi-project NextCompli console).
   */
  scopeChrome?: 'full' | 'minimal';
  /**
   * Host-shell iframe (jfgoss.com). Skips the duplicate Razzmatazz title block and
   * never surfaces NextCompli subscription / plan chips — the firm shell owns branding.
   */
  embedded?: boolean;
  /**
   * Host owns Traffic (native panel). Hide the Traffic pane and default elsewhere.
   */
  omitTraffic?: boolean;
  /**
   * Host draws the section nav. Render only the active pane body.
   */
  omitChrome?: boolean;
  /** Initial section (also controllable via razzmatazz:pane postMessage). */
  initialPane?: string;
  gatewayBaseUrl: string;
  accessToken: string;
  language?: 'en' | 'es';
  onSignIn?: () => void;
  onAccessDenied?: () => void;
  filmDesk?: boolean;
};

type Summary = {
  page_count: number;
  open_findings: number;
  drafts_waiting: number;
  approved_unpublished: number;
  published: number;
  citations_checked: number;
  citations_hit: number;
  keyword_count: number;
  live_backlinks: number;
  reports_ready: number;
};

type ProjectRow = {
  id: number;
  name: string;
  company_id: number;
  company_name?: string;
  tier_name?: string;
};

function projectOptionLabel(project: ProjectRow): string {
  const name = (project.name || '').trim();
  const company = (project.company_name || '').trim();
  if (!company || company.toLowerCase() === name.toLowerCase()) return name || company;
  return `${name} (${company})`;
}

function projectIdentityLabel(project: ProjectRow | undefined, spanish: boolean): string {
  if (!project) return spanish ? 'ninguno elegido' : 'none chosen';
  const name = (project.name || '').trim();
  const company = (project.company_name || '').trim();
  if (!company || company.toLowerCase() === name.toLowerCase()) return name || company;
  return `${name} · ${company}`;
}
type PageRow = { id: number; path: string; title?: string | null; h1?: string | null };
type FindingRow = { id: number; kind: string; severity: string; summary: string; status: string };
type DraftRow = {
  id: number;
  title: string;
  kind: string;
  status: string;
  platforms?: string | null;
  publish_result?: string | null;
};
type KeywordRow = { id: number; phrase: string; intent: string; position?: number | null };
type CitationRow = { id: number; query: string; engine: string; cited: boolean };
type LinkRow = { id: number; source_url: string; target_path: string; status: string };
type WorkRow = { id: number; channel: string; title: string; status: string };
type ReportRow = { id: number; title: string; cadence: string; status: string };

const PANES = ['traffic', 'schedules', 'social', 'bookings', 'pipeline', 'assent', 'studio', 'tuneup', 'pages', 'keywords', 'answers', 'drafts', 'links', 'board', 'recap', 'films'] as const;
type Pane = (typeof PANES)[number];

const paneLabel = (pane: Pane, spanish: boolean): string => {
  const labels: Record<Pane, [string, string]> = {
    traffic: ['Traffic', 'Tráfico'],
    schedules: ['Email & texts', 'Correo y textos'],
    social: ['Social mail', 'Correo social'],
    bookings: ['Bookings', 'Reservas'],
    pipeline: ['GTM pipeline', 'Pipeline GTM'],
    assent: ['Assent', 'Assent'],
    studio: ['Studio', 'Estudio'],
    tuneup: ['Tune-up', 'Ajuste'],
    pages: ['Pages', 'Páginas'],
    keywords: ['Keywords', 'Palabras clave'],
    answers: ['AI answers', 'Respuestas de IA'],
    drafts: ['Drafts', 'Borradores'],
    links: ['Links', 'Enlaces'],
    board: ['Board', 'Tablero'],
    recap: ['Reports', 'Reportes'],
    films: ['Films', 'Películas'],
  };
  return spanish ? labels[pane][1] : labels[pane][0];
};

const PRIMARY_PANES = ['traffic', 'schedules', 'social', 'bookings', 'pipeline', 'assent', 'recap'] as const;

function resolvePane(value: string | undefined, omitTraffic: boolean): Pane {
  const raw = (value || '').trim().toLowerCase();
  const hit = PANES.find((item) => item === raw);
  if (hit && !(omitTraffic && hit === 'traffic')) return hit;
  return omitTraffic ? 'schedules' : 'traffic';
}

const RazzmatazzWorkspace: React.FC<RazzmatazzWorkspaceProps> = ({
  gatewayBaseUrl,
  accessToken,
  language = 'en',
  preferredProjectName = '',
  trafficHost = '',
  scopeChrome = 'full',
  embedded = false,
  omitTraffic = false,
  omitChrome = false,
  initialPane,
  onSignIn,
  onAccessDenied,
  filmDesk = false,
}) => {
  const spanish = language === 'es';
  const hasSession = isRealGatewayAccessToken(accessToken);
  const [pane, setPane] = React.useState<Pane>(() => resolvePane(initialPane, omitTraffic));
  // Host embeds start compact: no plan bar, no second Razzmatazz title.
  const effectiveScope = embedded || omitChrome ? 'minimal' : scopeChrome;
  const [scopeExpanded, setScopeExpanded] = React.useState(effectiveScope === 'full' && !omitChrome);
  const needsProject = pane !== 'traffic' && pane !== 'schedules' && pane !== 'social' && pane !== 'bookings' && pane !== 'pipeline';
  const compactChrome = omitChrome || (effectiveScope === 'minimal' && !scopeExpanded && !needsProject);
  const showPlanChrome = !embedded && !omitChrome;
  const visiblePanes = React.useMemo(
    () => PANES.filter((item) => !(omitTraffic && item === 'traffic') && (item !== 'films' || filmDesk)),
    [omitTraffic, filmDesk],
  );
  const visiblePrimary = React.useMemo(
    () => PRIMARY_PANES.filter((item) => !(omitTraffic && item === 'traffic')),
    [omitTraffic],
  );

  React.useEffect(() => {
    setPane((current) => resolvePane(current, omitTraffic));
  }, [omitTraffic]);

  React.useEffect(() => {
    if (initialPane) setPane(resolvePane(initialPane, omitTraffic));
  }, [initialPane, omitTraffic]);

  React.useEffect(() => {
    if (!embedded && !omitChrome) return undefined;
    function onMessage(event: MessageEvent) {
      if (event.source !== window.parent) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || data.type !== 'razzmatazz:pane') return;
      setPane(resolvePane(String(data.pane || ''), omitTraffic));
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [embedded, omitChrome, omitTraffic]);

  React.useEffect(() => {
    if (omitChrome) return;
    if (needsProject && effectiveScope === 'minimal') {
      setScopeExpanded(true);
    }
  }, [needsProject, effectiveScope, omitChrome]);
  const [projectsLoaded, setProjectsLoaded] = React.useState(false);
  const [planName, setPlanName] = React.useState('');
  const [projects, setProjects] = React.useState<ProjectRow[]>([]);
  const [projectId, setProjectId] = React.useState<number | null>(null);
  const [createCompanyId, setCreateCompanyId] = React.useState<number | null>(null);
  const [projectDraft, setProjectDraft] = React.useState<{ mode: 'create' | 'rename'; name: string } | null>(null);
  const [summary, setSummary] = React.useState<Summary | null>(null);
  const [pages, setPages] = React.useState<PageRow[]>([]);
  const [findings, setFindings] = React.useState<FindingRow[]>([]);
  const [drafts, setDrafts] = React.useState<DraftRow[]>([]);
  const [keywords, setKeywords] = React.useState<KeywordRow[]>([]);
  const [citations, setCitations] = React.useState<CitationRow[]>([]);
  const [links, setLinks] = React.useState<LinkRow[]>([]);
  const [work, setWork] = React.useState<WorkRow[]>([]);
  const [reports, setReports] = React.useState<ReportRow[]>([]);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [path, setPath] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [finding, setFinding] = React.useState('');
  const [draftTitle, setDraftTitle] = React.useState('');
  const [draftKind, setDraftKind] = React.useState<'page' | 'social' | 'email' | 'answer' | 'ad'>('page');
  const [draftBody, setDraftBody] = React.useState('');
  const [draftPlatforms, setDraftPlatforms] = React.useState('facebook,linkedin,threads');
  const [phrase, setPhrase] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [sourceUrl, setSourceUrl] = React.useState('');
  const [workTitle, setWorkTitle] = React.useState('');
  const [reportTitle, setReportTitle] = React.useState('');
  const [stepStatus, setStepStatus] = React.useState<Record<string, string>>({});

  const selectedProject = projects.find((row) => row.id === projectId) || null;
  const accountId = selectedProject?.company_id ?? null;
  const studioOpen = Boolean(accountId && projectId);

  const authHeaders = React.useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const callApi = React.useCallback(
    async (apiPath: string, init?: RequestInit) => {
      if (!isRealGatewayAccessToken(accessToken)) {
        throw new Error(spanish ? 'Inicia sesión para usar Razzmatazz.' : 'Sign in with a gateway account to use Razzmatazz.');
      }
      const response = await fetch(`${gatewayBaseUrl}${apiPath}`, {
        ...init,
        headers: {
          ...authHeaders(),
          ...((init?.headers as Record<string, string> | undefined) || {}),
        },
      });
      const payload = (await response.json().catch(() => ({}))) as { detail?: unknown; items?: unknown };
      if (!response.ok) {
        const detail = typeof payload.detail === 'string' ? payload.detail : `Razzmatazz returned ${response.status}`;
        if (detail === 'Razzmatazz is not enabled.') {
          throw new Error(
            spanish
              ? 'El estudio está listo. El servicio de marketing todavía no está encendido en este entorno.'
              : 'The studio is ready. The marketing service is not turned on in this environment yet.',
          );
        }
        throw new Error(detail);
      }
      return payload;
    },
    [accessToken, authHeaders, gatewayBaseUrl, spanish],
  );

  const reloadProjects = React.useCallback(async () => {
    if (!hasSession) {
      return;
    }
    const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/workspace`, { headers: authHeaders() });
    if (!response.ok) {
      return;
    }
    const body = await response.json();
    setPlanName(String(body?.plan || '').trim());
    let rows = Array.isArray(body?.projects) ? body.projects : [];
    const companyIdsFromWorkspace = Array.isArray(body?.company_ids)
      ? body.company_ids.map((id: unknown) => Number(id)).filter((id: number) => id > 0)
      : [];
    // Host-shell embed (jfgoss) asks for a named project — create it once if the firm has none yet.
    const hintName = preferredProjectName.trim();
    if (!rows.length && hintName && companyIdsFromWorkspace[0]) {
      const created = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/projects`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ company_id: companyIdsFromWorkspace[0], name: hintName }),
      });
      if (created.ok) {
        const refresh = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/workspace`, { headers: authHeaders() });
        if (refresh.ok) {
          const refreshed = await refresh.json();
          rows = Array.isArray(refreshed?.projects) ? refreshed.projects : rows;
          setPlanName(String(refreshed?.plan || body?.plan || '').trim());
        }
      }
    }
    const next = rows
      .map((row: { id?: number; name?: string; company_id?: number; company_name?: string; tier_name?: string }) => ({
        id: Number(row.id),
        name: String(row.name || '').trim(),
        company_id: Number(row.company_id),
        company_name: String(row.company_name || '').trim(),
        tier_name: String(row.tier_name || '').trim(),
      }))
      .filter((row: ProjectRow) => row.id && row.name && row.company_id);
    setProjects(next);
    setProjectsLoaded(true);
    setProjectId((current) => {
      if (current && next.some((row: ProjectRow) => row.id === current)) {
        return current;
      }
      const hint = preferredProjectName.trim().toLowerCase();
      const preferred = (hint
        ? next.find((row: ProjectRow) => row.name.toLowerCase() === hint
          || row.name.toLowerCase().includes(hint)
          || (row.company_name || '').toLowerCase().includes(hint))
        : null)
        || next.find((row: ProjectRow) => /gossled/i.test(row.name))
        || next.find((row: ProjectRow) => /canyon/i.test(row.name))
        || next[0];
      return preferred?.id ?? null;
    });
    const companyIds = Array.from(new Set([
      ...next.map((row: ProjectRow) => row.company_id),
      ...companyIdsFromWorkspace,
    ]));
    setCreateCompanyId((current) => current && companyIds.includes(current) ? current : (companyIds[0] ?? null));
  }, [authHeaders, gatewayBaseUrl, hasSession, preferredProjectName]);

  React.useEffect(() => {
    if (!hasSession) {
      return undefined;
    }
    void reloadProjects();
    return undefined;
  }, [hasSession, reloadProjects]);

  React.useEffect(() => {
    if (!studioOpen || !accountId || !projectId) {
      setStepStatus({});
      return undefined;
    }
    let cancelled = false;
    const loadWorkflow = async () => {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/razzmatazz/workflow?company_id=${accountId}&project_id=${projectId}`,
        { headers: authHeaders() },
      );
      if (!response.ok || cancelled) {
        return;
      }
      const body = await response.json();
      const rows = Array.isArray(body?.items) ? body.items : [];
      if (!cancelled) {
        const next: Record<string, string> = {};
        rows.forEach((row: { step_id?: string; status?: string }) => {
          if (row.step_id) {
            next[String(row.step_id)] = String(row.status || 'not_started');
          }
        });
        setStepStatus(next);
      }
    };
    void loadWorkflow();
    return () => {
      cancelled = true;
    };
  }, [accountId, authHeaders, gatewayBaseUrl, projectId, studioOpen]);

  const markStep = async (stepId: string, status: string) => {
    if (!accountId || !projectId) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/workflow`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ company_id: accountId, project_id: projectId, step_id: stepId, status }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Could not save the workflow step.');
      }
      setStepStatus((current) => ({ ...current, [stepId]: status }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const scopeQuery = studioOpen ? `company_id=${accountId}&project_id=${projectId}` : '';

  const load = React.useCallback(async () => {
    if (!studioOpen) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      const [summaryBody, pageBody, findingBody, draftBody, keywordBody, citationBody, linkBody, workBody, reportBody] =
        await Promise.all([
          callApi(`/api/razzmatazz/summary?${scopeQuery}`),
          callApi(`/api/razzmatazz/pages?${scopeQuery}`),
          callApi(`/api/razzmatazz/findings?${scopeQuery}`),
          callApi(`/api/razzmatazz/drafts?${scopeQuery}`),
          callApi(`/api/razzmatazz/keywords?${scopeQuery}`),
          callApi(`/api/razzmatazz/citations?${scopeQuery}`),
          callApi(`/api/razzmatazz/backlinks?${scopeQuery}`),
          callApi(`/api/razzmatazz/work?${scopeQuery}`),
          callApi(`/api/razzmatazz/reports?${scopeQuery}`),
        ]);
      setSummary(summaryBody as Summary);
      setPages((pageBody.items as PageRow[]) || []);
      setFindings((findingBody.items as FindingRow[]) || []);
      setDrafts((draftBody.items as DraftRow[]) || []);
      setKeywords((keywordBody.items as KeywordRow[]) || []);
      setCitations((citationBody.items as CitationRow[]) || []);
      setLinks((linkBody.items as LinkRow[]) || []);
      setWork((workBody.items as WorkRow[]) || []);
      setReports((reportBody.items as ReportRow[]) || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (isRoleAccessDeniedMessage(message)) {
        onAccessDenied?.();
        return;
      }
      setError(message);
    } finally {
      setBusy(false);
    }
  }, [callApi, onAccessDenied, scopeQuery, studioOpen]);

  React.useEffect(() => {
    if (studioOpen) {
      void load();
    }
  }, [load, studioOpen]);

  const post = async (apiPath: string, body: Record<string, unknown>) => {
    setBusy(true);
    setError('');
    try {
      await callApi(apiPath, {
        method: 'POST',
        body: JSON.stringify({ ...body, company_id: accountId, project_id: projectId }),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const saveProject = async (event: React.FormEvent) => {
    event.preventDefault();
    if (projectDraft == null) {
      return;
    }
    const name = projectDraft.name.trim();
    if (!name) {
      return;
    }
    const renaming = projectDraft.mode === 'rename' ? projectId : null;
    const ownerCompanyId = renaming
      ? accountId
      : (createCompanyId || accountId || selectedProject?.company_id || null);
    if (!ownerCompanyId) {
      setError(spanish ? 'No hay un espacio de trabajo para el proyecto.' : 'No workspace is available for the project.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        renaming
          ? `${gatewayBaseUrl}/api/v1/razzmatazz/projects/${renaming}/rename`
          : `${gatewayBaseUrl}/api/v1/razzmatazz/projects`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ company_id: ownerCompanyId, name }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Could not save the project.');
      }
      await reloadProjects();
      setProjectId(Number(payload.id) || null);
      setProjectDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const companyChoices = React.useMemo(() => {
    const map = new Map<number, string>();
    projects.forEach((row) => {
      if (row.company_id && !map.has(row.company_id)) {
        map.set(row.company_id, row.company_name || `Workspace ${row.company_id}`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [projects]);

  if (!hasSession) {
    return (
      <section className="razz-console" aria-label="Razzmatazz">
        <div className="razz-signin">
          <p className="razz-kicker">NextCompli Razzmatazz</p>
          <h3>{spanish ? 'Entra al estudio' : 'Come into the studio'}</h3>
          <p>
            {spanish
              ? 'El navegador habla con el gateway, no con Razzmatazz. Pursuit sigue siendo el CRM. Inlet sigue siendo la entrada.'
              : 'The browser talks to the gateway, not Razzmatazz. Pursuit stays the CRM. Inlet stays inbound.'}
          </p>
          {onSignIn ? (
            <button type="button" className="dashboard-security-inline-btn" onClick={onSignIn}>
              {spanish ? 'Iniciar sesión' : 'Sign in'}
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section
      className={`razz-console${compactChrome ? ' is-compact-chrome' : ''}${embedded ? ' is-embedded' : ''}${omitChrome ? ' is-pane-only' : ''}`}
      aria-label="Razzmatazz"
    >
      {!embedded && !omitChrome ? (
      <header className="razz-chrome">
        <div>
          {effectiveScope === 'full' ? <p className="razz-kicker">NextCompli Razzmatazz</p> : null}
          <h2>Razzmatazz</h2>
          {!compactChrome ? (
            <p className="razz-lede">
              {spanish
                ? 'Elige el proyecto. Tu plan es el nivel más alto que pagas en este correo. Los administradores del proyecto controlan quién entra. Los borradores sociales se publican vía Socials tras el sí.'
                : 'Pick the project. Your plan is the highest level this email pays for. Project admins control who gets in. Social drafts publish through Socials after the yes.'}
            </p>
          ) : (
            <p className="razz-lede">
              {spanish
                ? 'Tráfico, correo y borradores del sitio. El plan y el proyecto solo hacen falta para el estudio.'
                : 'Site traffic, mail, and drafts. Plan and project only matter for the studio board.'}
            </p>
          )}
        </div>
        <div className="razz-header-actions">
          {effectiveScope === 'minimal' ? (
            <button
              type="button"
              className="dashboard-btn"
              aria-expanded={scopeExpanded}
              onClick={() => setScopeExpanded((open) => !open)}
            >
              {scopeExpanded
                ? (spanish ? 'Ocultar proyecto' : 'Hide project')
                : (spanish ? 'Proyecto y plan' : 'Project & plan')}
            </button>
          ) : null}
          {showPlanChrome && !compactChrome && planName ? <span className="razz-plan-chip">{planName}</span> : null}
          {studioOpen ? (
            <button type="button" className="dashboard-btn" onClick={() => void load()} disabled={busy}>
              {busy ? (spanish ? 'Cargando…' : 'Loading…') : (spanish ? 'Actualizar' : 'Refresh')}
            </button>
          ) : null}
        </div>
      </header>
      ) : !omitChrome && effectiveScope === 'minimal' && (needsProject || scopeExpanded) ? (
        <div className="razz-chrome razz-chrome--embed-tools">
          <div className="razz-header-actions">
            <button
              type="button"
              className="dashboard-btn"
              aria-expanded={scopeExpanded}
              onClick={() => setScopeExpanded((open) => !open)}
            >
              {scopeExpanded
                ? (spanish ? 'Ocultar proyecto' : 'Hide project')
                : (spanish ? 'Cambiar proyecto' : 'Switch project')}
            </button>
            {studioOpen ? (
              <button type="button" className="dashboard-btn" onClick={() => void load()} disabled={busy}>
                {busy ? (spanish ? 'Cargando…' : 'Loading…') : (spanish ? 'Actualizar' : 'Refresh')}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {(!omitChrome && !compactChrome) || (omitChrome && needsProject) ? (
      <div className={`dod-scope-bar${!studioOpen ? ' is-first-step' : ''}${omitChrome ? ' is-host-owned' : ''}`}>
        <p className="compliance-scope-identity" aria-label={spanish ? 'Qué está abierto' : 'What is open'}>
          {showPlanChrome ? (
            <span>
              <strong>{spanish ? 'Plan' : 'Plan'}:</strong>
              {' '}
              {planName || (spanish ? 'sin plan' : 'none')}
            </span>
          ) : null}
          <span>
            <strong>{spanish ? 'Proyecto' : 'Project'}:</strong>
            {' '}
            {projectIdentityLabel(selectedProject, spanish)}
          </span>
        </p>
        <div className="dod-scope-filters">
          <div className="compliance-console-company dod-scope-picker dod-scope-field">
            <div className="dod-scope-picker-label">
              <label htmlFor="razz-project-select">{spanish ? 'Proyecto' : 'Project'}</label>
              <HelpBadge label={spanish ? 'Cómo elegir un proyecto' : 'How to pick a project'}>
                <p>
                  {embedded
                    ? (spanish
                      ? 'Un proyecto es el trabajo de marketing (GossLed, una campaña). Los administradores del proyecto deciden quién entra.'
                      : 'A project is the marketing work (GossLed, a campaign). Project admins decide who gets in.')
                    : (spanish
                      ? 'Un proyecto es el trabajo (GossLed, Canyon Foundry, una campaña). No eliges un nivel de suscripción por proyecto: el correo guarda el nivel más alto. Los administradores del proyecto deciden quién entra.'
                      : 'A project is the work (GossLed, Canyon Foundry, a campaign). You do not pick a subscription level per project — the email keeps the highest paid plan. Project admins decide who gets in.')}
                </p>
              </HelpBadge>
            </div>
            <div className="dod-scope-control">
              <select
                id="razz-project-select"
                className="dod-scope-select"
                value={projectId ?? ''}
                onChange={(event) => {
                  setProjectDraft(null);
                  const next = Number(event.target.value);
                  setProjectId(next || null);
                }}
              >
                <option value="">{spanish ? 'Seleccionar proyecto' : 'Select project'}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {projectOptionLabel(project)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="dashboard-btn compliance-add-account"
                onClick={() => setProjectDraft({ mode: 'create', name: '' })}
              >
                {spanish ? 'Añadir proyecto' : 'Add project'}
              </button>
              <button
                type="button"
                className="dashboard-btn"
                disabled={!selectedProject}
                onClick={() => setProjectDraft({ mode: 'rename', name: selectedProject?.name || '' })}
              >
                {spanish ? 'Cambiar nombre' : 'Rename'}
              </button>
            </div>
            {projectsLoaded && projects.length === 0 && !projectDraft ? (
              <p className="compliance-console-company-hint" role="status">
                {spanish ? 'Todavía no hay proyectos.' : 'No projects are saved yet.'}
              </p>
            ) : null}
            {projectDraft ? (
              <form className="dod-register-form razz-form" onSubmit={(event) => void saveProject(event)}>
                <label htmlFor="razz-project-name">
                  {spanish ? 'Nombre del proyecto' : 'Project name'}
                  <input
                    id="razz-project-name"
                    value={projectDraft.name}
                    onChange={(event) => setProjectDraft({ mode: projectDraft.mode, name: event.target.value })}
                    required
                    placeholder={spanish ? 'Campaña, marca o cliente' : 'Campaign, brand, or client'}
                  />
                </label>
                {projectDraft.mode === 'create' && companyChoices.length > 1 ? (
                  <label htmlFor="razz-project-owner">
                    {spanish ? 'Espacio de trabajo' : 'Workspace'}
                    <select
                      id="razz-project-owner"
                      value={createCompanyId ?? ''}
                      onChange={(event) => setCreateCompanyId(Number(event.target.value) || null)}
                    >
                      {companyChoices.map((row) => (
                        <option key={row.id} value={row.id}>{row.name}</option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <button type="submit" className="dashboard-btn" disabled={busy}>
                  {spanish ? 'Guardar proyecto' : 'Save project'}
                </button>
                <button type="button" className="dashboard-btn" onClick={() => setProjectDraft(null)}>
                  {spanish ? 'Cancelar' : 'Cancel'}
                </button>
              </form>
            ) : null}
          </div>
        </div>
      </div>
      ) : null}

      {!omitChrome ? (
      <nav className="razz-panes" aria-label={spanish ? 'Secciones de Razzmatazz' : 'Razzmatazz sections'}>
        <div className="razz-panes-primary" role="presentation">
          {visiblePrimary.map((item) => (
            <button key={item} type="button" aria-pressed={pane === item} onClick={() => setPane(item)}>
              {paneLabel(item, spanish)}
            </button>
          ))}
        </div>
        <div className="razz-panes-rest" role="presentation">
          {visiblePanes.filter((item) => !(PRIMARY_PANES as readonly string[]).includes(item)).map((item) => (
            <button key={item} type="button" aria-pressed={pane === item} onClick={() => setPane(item)}>
              {paneLabel(item, spanish)}
            </button>
          ))}
        </div>
      </nav>
      ) : null}
      {error ? <p className="razz-error" role="alert">{error}</p> : null}
      {pane === 'traffic' && !omitTraffic ? (
        <RazzmatazzTrafficPanel
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          language={language}
          host={trafficHost}
        />
      ) : null}
      {pane === 'schedules' ? (
        <RazzmatazzNotifyPanel
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          language={language}
        />
      ) : null}
      {pane === 'social' ? (
        <RazzmatazzSocialIntakePanel
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          companyId={accountId}
          projectId={projectId}
          language={language}
        />
      ) : null}
      {pane === 'bookings' ? (
        <RazzmatazzBookingPanel
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          companyId={accountId}
          language={language}
        />
      ) : null}
      {pane === 'pipeline' ? (
        <RazzmatazzPipelinePanel
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          companyId={accountId}
          language={language}
        />
      ) : null}
      {pane === 'assent' ? (
        <AssentWorkspace
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          companyId={accountId}
          language={language}
        />
      ) : null}
      {pane === 'studio' ? (
        studioOpen ? (
          <>
            {summary ? (
              <div className="razz-stats">
                <div><strong>{summary.page_count}</strong><span>{spanish ? 'Páginas' : 'Pages'}</span></div>
                <div><strong>{summary.open_findings}</strong><span>{spanish ? 'Por afinar' : 'To tune'}</span></div>
                <div><strong>{summary.keyword_count}</strong><span>{spanish ? 'Palabras clave' : 'Keywords'}</span></div>
                <div><strong>{summary.citations_hit}</strong><span>{spanish ? 'Respuestas que citan' : 'Citing answers'}</span></div>
                <div><strong>{summary.drafts_waiting}</strong><span>{spanish ? 'Borradores' : 'Drafts'}</span></div>
                <div><strong>{summary.live_backlinks}</strong><span>{spanish ? 'Enlaces vivos' : 'Live links'}</span></div>
                <div><strong>{summary.reports_ready}</strong><span>{spanish ? 'Recaps listos' : 'Recaps ready'}</span></div>
              </div>
            ) : null}
            <RazzmatazzWorkflowBoard
              spanish={spanish}
              planName={planName}
              studioOpen={studioOpen}
              statusById={stepStatus}
              busy={busy}
              onMark={(stepId, status) => void markStep(stepId, status)}
            />
          </>
        ) : (
          <p className="razz-empty" role="status">
            {spanish
              ? 'Elige un proyecto para el flujo de Studio. Tráfico, Correo, Social, Reservas, Pipeline y Assent no lo necesitan.'
              : 'Pick a project for the Studio workflow. Traffic, Email, Social, Bookings, Pipeline, and Assent do not need one.'}
          </p>
        )
      ) : null}
      {pane !== 'traffic' && pane !== 'schedules' && pane !== 'social' && pane !== 'bookings' && pane !== 'pipeline' && pane !== 'assent' && pane !== 'studio' && !studioOpen ? (
        <p className="razz-empty" role="status">
          {spanish
            ? 'Elige un proyecto para esta sección.'
            : 'Pick a project for this section.'}
        </p>
      ) : null}
      {pane !== 'traffic' && pane !== 'schedules' && pane !== 'social' && pane !== 'bookings' && pane !== 'assent' && pane !== 'studio' && studioOpen ? (
        <>
          {pane === 'pages' ? (
            <article className="razz-card">
              <form
                className="razz-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void post('/api/razzmatazz/pages', { path, title });
                  setPath('');
                  setTitle('');
                }}
              >
                <input aria-label={spanish ? 'Ruta' : 'Path'} value={path} onChange={(event) => setPath(event.target.value)} required placeholder="/plans" />
                <input aria-label={spanish ? 'Título' : 'Title'} value={title} onChange={(event) => setTitle(event.target.value)} placeholder={spanish ? 'Título' : 'Title'} />
                <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>{spanish ? 'Añadir página' : 'Add page'}</button>
              </form>
              <ul>{pages.map((row) => <li key={row.id}>{row.path}{row.title ? ` — ${row.title}` : ''}</li>)}</ul>
            </article>
          ) : null}
          {pane === 'tuneup' ? (
            <article className="razz-card">
              <form
                className="razz-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void post('/api/razzmatazz/findings', { kind: 'on-page', severity: 'medium', summary: finding });
                  setFinding('');
                }}
              >
                <input aria-label={spanish ? 'Ajuste' : 'Tune-up'} value={finding} onChange={(event) => setFinding(event.target.value)} required placeholder={spanish ? 'Qué hay que afinar' : 'What needs a tune-up'} />
                <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>{spanish ? 'Registrar' : 'Add tune-up'}</button>
              </form>
              <ul>
                {findings.map((row) => (
                  <li key={row.id}>
                    {row.severity}: {row.summary} ({row.status})
                    {row.status === 'open' ? (
                      <button type="button" onClick={() => void post(`/api/razzmatazz/findings/${row.id}/resolve`, {})}>{spanish ? 'Listo' : 'Done'}</button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </article>
          ) : null}
          {pane === 'keywords' ? (
            <article className="razz-card">
              <form
                className="razz-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void post('/api/razzmatazz/keywords', { phrase, intent: 'commercial' });
                  setPhrase('');
                }}
              >
                <input aria-label={spanish ? 'Palabra clave' : 'Keyword'} value={phrase} onChange={(event) => setPhrase(event.target.value)} required placeholder={spanish ? 'La frase que quieres' : 'The phrase you want'} />
                <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>{spanish ? 'Seguir' : 'Track keyword'}</button>
              </form>
              <ul>{keywords.map((row) => <li key={row.id}>{row.phrase} · {row.intent}</li>)}</ul>
            </article>
          ) : null}
          {pane === 'answers' ? (
            <article className="razz-card">
              <form
                className="razz-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void post('/api/razzmatazz/citations', { query, engine: 'perplexity', cited: false });
                  setQuery('');
                }}
              >
                <input aria-label={spanish ? 'Consulta' : 'Query'} value={query} onChange={(event) => setQuery(event.target.value)} required placeholder={spanish ? 'Qué le preguntan a la IA' : 'What people ask the AI'} />
                <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>{spanish ? 'Vigilar' : 'Watch query'}</button>
              </form>
              <ul>{citations.map((row) => <li key={row.id}>{row.engine}: {row.query}{row.cited ? (spanish ? ' · citada' : ' · cited') : ''}</li>)}</ul>
            </article>
          ) : null}
          {pane === 'drafts' ? (
            <article className="razz-card">
              <form
                className="razz-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const isSocial = draftKind === 'social';
                  void post('/api/razzmatazz/drafts', {
                    kind: draftKind,
                    title: draftTitle,
                    body: draftBody,
                    ...(isSocial ? { platforms: draftPlatforms } : {}),
                  });
                  setDraftTitle('');
                  setDraftBody('');
                }}
              >
                <select
                  aria-label={spanish ? 'Tipo de borrador' : 'Draft kind'}
                  value={draftKind}
                  onChange={(event) => setDraftKind(event.target.value as 'page' | 'social' | 'email' | 'answer' | 'ad')}
                >
                  <option value="page">{spanish ? 'Página' : 'Page'}</option>
                  <option value="social">{spanish ? 'Social (vía Socials)' : 'Social (via Socials)'}</option>
                  <option value="email">Email</option>
                  <option value="answer">{spanish ? 'Respuesta' : 'Answer'}</option>
                  <option value="ad">{spanish ? 'Anuncio' : 'Ad'}</option>
                </select>
                <input aria-label={spanish ? 'Título del borrador' : 'Draft title'} value={draftTitle} onChange={(event) => setDraftTitle(event.target.value)} required placeholder={spanish ? 'Título del borrador' : 'Draft title'} />
                {draftKind === 'social' ? (
                  <>
                    <textarea
                      aria-label={spanish ? 'Texto del post' : 'Post body'}
                      value={draftBody}
                      onChange={(event) => setDraftBody(event.target.value)}
                      required
                      placeholder={spanish ? 'Texto que se publicará después del sí' : 'Copy that Socials will post after the yes'}
                      rows={3}
                    />
                    <input
                      aria-label={spanish ? 'Plataformas' : 'Platforms'}
                      value={draftPlatforms}
                      onChange={(event) => setDraftPlatforms(event.target.value)}
                      placeholder="facebook,linkedin,threads"
                    />
                  </>
                ) : null}
                <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>{spanish ? 'Crear borrador' : 'Create draft'}</button>
              </form>
              <p className="razz-muted">
                {spanish
                  ? 'Los borradores sociales se envían a SocialsMCP tras Aprobar → Marcar en vivo (SOCIALS_ADAPTER_URL).'
                  : 'Social drafts push through SocialsMCP after Approve → Mark live (SOCIALS_ADAPTER_URL).'}
              </p>
              <ul>
                {drafts.map((row) => (
                  <li key={row.id}>
                    {row.title} · {row.kind} · {row.status}
                    {row.platforms ? ` · ${row.platforms}` : ''}
                    {row.status === 'draft' ? <button type="button" onClick={() => void post(`/api/razzmatazz/drafts/${row.id}/submit`, {})}>{spanish ? 'Enviar' : 'Submit'}</button> : null}
                    {row.status === 'in_review' ? <button type="button" onClick={() => void post(`/api/razzmatazz/drafts/${row.id}/approve`, {})}>{spanish ? 'Aprobar' : 'Approve'}</button> : null}
                    {row.status === 'approved' ? <button type="button" onClick={() => void post(`/api/razzmatazz/drafts/${row.id}/publish`, {})}>{spanish ? 'Marcar en vivo' : 'Mark live'}</button> : null}
                    {row.publish_result ? (
                      <span className="razz-muted"> · {row.publish_result.includes('record_only') ? (spanish ? 'solo registro' : 'record only') : (spanish ? 'enviado a Socials' : 'pushed via Socials')}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </article>
          ) : null}
          {pane === 'links' ? (
            <article className="razz-card">
              <form
                className="razz-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void post('/api/razzmatazz/backlinks', { source_url: sourceUrl, target_path: '/', status: 'prospect' });
                  setSourceUrl('');
                }}
              >
                <input aria-label={spanish ? 'URL de origen' : 'Source URL'} value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} required placeholder="https://" />
                <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>{spanish ? 'Añadir prospecto' : 'Add prospect'}</button>
              </form>
              <ul>{links.map((row) => <li key={row.id}>{row.status}: {row.source_url} → {row.target_path}</li>)}</ul>
            </article>
          ) : null}
          {pane === 'board' ? (
            <article className="razz-card razz-board">
              <div className="razz-board-intro">
                <h3>{spanish ? 'Tablero de incidentes y movimientos' : 'Incident & move board'}</h3>
                <p className="razz-muted">
                  {spanish
                    ? 'Carriles como el escritorio: afina hallazgos arriba, mueve trabajo abajo. Studio es el flujo por pasos; este tablero es el kanban.'
                    : 'Swim lanes like the desk: tune-up findings on top, marketing moves below. Studio is the step workflow; this board is the kanban.'}
                </p>
              </div>
              <section className="razz-swim" aria-label={spanish ? 'Hallazgos' : 'Findings'}>
                <h4>{spanish ? 'Incidentes (ajuste)' : 'Incidents (tune-up)'}</h4>
                <div className="razz-swim-lanes">
                  {(
                    [
                      { id: 'open', en: 'Open', es: 'Abierto', match: (s: string) => s !== 'resolved' },
                      { id: 'resolved', en: 'Done', es: 'Listo', match: (s: string) => s === 'resolved' },
                    ] as const
                  ).map((lane) => {
                    const rows = findings.filter((row) => lane.match(String(row.status || '').toLowerCase()));
                    return (
                      <div key={lane.id} className="razz-swim-lane">
                        <header>
                          <strong>{spanish ? lane.es : lane.en}</strong>
                          <span>{rows.length}</span>
                        </header>
                        <ul>
                          {rows.length === 0 ? (
                            <li className="razz-muted">{spanish ? 'Vacío' : 'Empty'}</li>
                          ) : (
                            rows.map((row) => (
                              <li key={row.id} className="razz-swim-card">
                                <span className="razz-swim-card-meta">{row.severity} · {row.kind}</span>
                                <strong>{row.summary}</strong>
                                {row.status !== 'resolved' ? (
                                  <button
                                    type="button"
                                    className="dashboard-security-inline-btn"
                                    disabled={busy}
                                    onClick={() => void post(`/api/razzmatazz/findings/${row.id}/resolve`, {})}
                                  >
                                    {spanish ? 'Listo' : 'Done'}
                                  </button>
                                ) : null}
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </section>
              <section className="razz-swim" aria-label={spanish ? 'Movimientos' : 'Moves'}>
                <h4>{spanish ? 'Movimientos de marketing' : 'Marketing moves'}</h4>
                <form
                  className="razz-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void post('/api/razzmatazz/work', { channel: 'content', title: workTitle });
                    setWorkTitle('');
                  }}
                >
                  <input aria-label={spanish ? 'Movimiento' : 'Move'} value={workTitle} onChange={(event) => setWorkTitle(event.target.value)} required placeholder={spanish ? 'El siguiente movimiento' : 'The next move'} />
                  <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>{spanish ? 'Poner en el tablero' : 'Put on the board'}</button>
                </form>
                <div className="razz-swim-lanes razz-swim-lanes-4">
                  {(
                    [
                      { id: 'planned', en: 'Planned', es: 'Planeado' },
                      { id: 'doing', en: 'Doing', es: 'En curso' },
                      { id: 'blocked', en: 'Blocked', es: 'Bloqueado' },
                      { id: 'done', en: 'Done', es: 'Hecho' },
                    ] as const
                  ).map((lane) => {
                    const rows = work.filter((row) => String(row.status || 'planned').toLowerCase() === lane.id);
                    const nextFor = (status: string): Array<{ id: string; en: string; es: string }> => {
                      if (status === 'planned') return [{ id: 'doing', en: 'Start', es: 'Empezar' }, { id: 'blocked', en: 'Block', es: 'Bloquear' }];
                      if (status === 'doing') return [{ id: 'blocked', en: 'Block', es: 'Bloquear' }, { id: 'done', en: 'Done', es: 'Hecho' }];
                      if (status === 'blocked') return [{ id: 'doing', en: 'Resume', es: 'Retomar' }, { id: 'done', en: 'Done', es: 'Hecho' }];
                      return [{ id: 'planned', en: 'Reopen', es: 'Reabrir' }];
                    };
                    return (
                      <div key={lane.id} className="razz-swim-lane">
                        <header>
                          <strong>{spanish ? lane.es : lane.en}</strong>
                          <span>{rows.length}</span>
                        </header>
                        <ul>
                          {rows.length === 0 ? (
                            <li className="razz-muted">{spanish ? 'Vacío' : 'Empty'}</li>
                          ) : (
                            rows.map((row) => (
                              <li key={row.id} className="razz-swim-card">
                                <span className="razz-swim-card-meta">{row.channel}</span>
                                <strong>{row.title}</strong>
                                <div className="razz-swim-card-actions">
                                  {nextFor(lane.id).map((action) => (
                                    <button
                                      key={action.id}
                                      type="button"
                                      className="dashboard-security-inline-btn"
                                      disabled={busy}
                                      onClick={() => void post(`/api/razzmatazz/work/${row.id}/status`, { status: action.id })}
                                    >
                                      {spanish ? action.es : action.en}
                                    </button>
                                  ))}
                                </div>
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </section>
            </article>
          ) : null}
          {pane === 'films' && filmDesk ? <MarketingStudioPage /> : null}
          {pane === 'recap' ? (
            <article className="razz-card razz-reports">
              <header className="razz-board-intro">
                <h3>{spanish ? 'Reportes de marketing' : 'Marketing reports'}</h3>
                <p className="razz-muted">
                  {spanish
                    ? 'Lo que suelen pedir HubSpot, GA4 y Semrush: digest diario, recap semanal/mensual y un resumen ejecutivo. El digest diario se configura en Correo y textos.'
                    : 'What HubSpot, GA4, and Semrush operators expect: a daily digest, weekly/monthly recaps, and an executive summary. Configure the daily digest under Email & texts.'}
                </p>
              </header>
              <ul className="razz-report-catalog" aria-label={spanish ? 'Tipos de reporte' : 'Report types'}>
                <li className="razz-report-type">
                  <strong>{spanish ? 'Digest diario' : 'Daily digest'}</strong>
                  <span className="razz-muted">
                    {spanish
                      ? 'Tráfico del día (visitantes, canales, páginas). Se envía desde Correo y textos.'
                      : 'Same-day traffic (visitors, channels, top pages). Sent from Email & texts.'}
                  </span>
                  <button type="button" className="dashboard-security-inline-btn" onClick={() => setPane('schedules')}>
                    {spanish ? 'Configurar envío' : 'Configure delivery'}
                  </button>
                </li>
                <li className="razz-report-type">
                  <strong>{spanish ? 'Recap semanal' : 'Weekly recap'}</strong>
                  <span className="razz-muted">
                    {spanish
                      ? 'Resumen de la semana: tráfico, borradores y movimientos del tablero.'
                      : 'Week in review: traffic, drafts, and board moves.'}
                  </span>
                </li>
                <li className="razz-report-type">
                  <strong>{spanish ? 'Recap mensual' : 'Monthly recap'}</strong>
                  <span className="razz-muted">
                    {spanish
                      ? 'Tendencias del mes y hallazgos de ajuste que siguen abiertos.'
                      : 'Month trends plus tune-up findings still open.'}
                  </span>
                </li>
                <li className="razz-report-type">
                  <strong>{spanish ? 'Resumen ejecutivo' : 'Executive summary'}</strong>
                  <span className="razz-muted">
                    {spanish
                      ? 'Narrativa corta para liderazgo (requiere permiso de reportes ejecutivos).'
                      : 'Short leadership narrative (needs executive report permission).'}
                  </span>
                </li>
              </ul>
              <form
                className="razz-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void post('/api/razzmatazz/reports', { title: reportTitle, cadence: 'weekly' });
                  setReportTitle('');
                }}
              >
                <input
                  aria-label={spanish ? 'Título del recap' : 'Recap title'}
                  value={reportTitle}
                  onChange={(event) => setReportTitle(event.target.value)}
                  required
                  placeholder={spanish ? 'Recap de la semana' : 'This week’s recap'}
                />
                <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
                  {spanish ? 'Crear recap semanal' : 'Create weekly recap'}
                </button>
              </form>
              <ul className="razz-report-list">
                {reports.length === 0 ? (
                  <li className="razz-muted">
                    {spanish
                      ? 'Aún no hay recaps. Crea uno arriba o espera el digest diario.'
                      : 'No recaps yet. Create one above, or wait for the daily digest.'}
                  </li>
                ) : (
                  reports.map((row) => (
                    <li key={row.id}>
                      <strong>{row.title}</strong>
                      <span className="razz-muted">
                        {row.cadence}
                        {' · '}
                        {row.status}
                      </span>
                      {row.status === 'draft' ? (
                        <button type="button" onClick={() => void post(`/api/razzmatazz/reports/${row.id}/ready`, {})}>
                          {spanish ? 'Listo' : 'Mark ready'}
                        </button>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            </article>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

const capabilityCopy = (capability: RazzmatazzCapability, spanish: boolean): { heading: string; help: string } => {
  if (capability === 'CompliCore') {
    return {
      heading: 'CompliCore',
      help: spanish
        ? 'CompliCore es el estudio a mano: brief, páginas, frases, borrador, el sí de una persona, la marca en vivo y el recap.'
        : 'CompliCore is the studio you run by hand: the brief, the pages, the phrases, the draft, a person’s yes, the live mark, and the recap.',
    };
  }
  if (capability === 'CompliAdvantage') {
    return {
      heading: 'CompliAdvantage',
      help: spanish
        ? 'CompliAdvantage añade las vigilancias: dónde quedan las frases, si una respuesta de IA te cita, prospectos de enlaces y un recap que se repite.'
        : 'CompliAdvantage adds the watches: where the phrases sit, whether an AI answer cites you, link prospects, and a recap that repeats.',
    };
  }
  return {
    heading: 'CompliComplete',
    help: spanish
      ? 'CompliComplete es la capa gobernada: consentimiento, a quién dejar fuera, un límite de contacto, una audiencia con nombre, un recorrido escrito, un puntaje con razón, la entrega a Pursuit, una explicación del resultado, la revisión del dominio de envío, los temas, una prueba con nombre, nombres duplicados y el escritorio de video. Una persona sigue marcando cada paso.'
      : 'CompliComplete is the governed layer: consent, who to leave out, a contact cap, a named audience, a written path, a score with a reason, the handoff to Pursuit, a plain explanation of the result, the sending-domain check, topic choices, a named test, duplicate names, and the film desk. A person still marks each step.',
  };
};

const RazzmatazzWorkflowBoard: React.FC<{
  spanish: boolean;
  planName: string;
  studioOpen: boolean;
  statusById: Record<string, string>;
  busy: boolean;
  onMark: (stepId: string, status: string) => void;
}> = ({ spanish, planName, studioOpen, statusById, busy, onMark }) => {
  const plan = planFromTierName(planName);
  const byId = new Map(RAZZMATAZZ_WORKFLOW.map((step) => [step.id, step]));
  const renderStep = (step: RazzmatazzStep) => {
    const included = stepIncludedInPlan(step, plan);
    const earlier = step.dependsOn ? byId.get(step.dependsOn) : undefined;
    const earlierDone = !earlier || statusById[earlier.id] === 'done';
    const status = statusById[step.id] || 'not_started';
    const waiting = included && studioOpen && !earlierDone && status !== 'done';
    return (
      <li key={step.id} className={`razz-step${included ? '' : ' is-locked'}${status === 'done' ? ' is-done' : ''}`}>
        <div>
          <strong>{spanish ? step.title[1] : step.title[0]}</strong>
          <p>{spanish ? step.action[1] : step.action[0]}</p>
          <p className="razz-step-meta">
            {status === 'done'
              ? (spanish ? 'Hecho' : 'Done')
              : waiting
                ? (spanish ? `Espera: ${earlier ? earlier.title[1] : ''}` : `Waiting on ${earlier ? earlier.title[0] : ''}`)
                : included
                  ? (spanish ? 'Listo para hacer' : 'Ready')
                  : step.capability}
          </p>
        </div>
        {included && studioOpen ? (
          <div className="razz-step-actions">
            {status !== 'done' ? (
              <button type="button" className="dashboard-btn" disabled={busy || waiting} onClick={() => onMark(step.id, 'in_progress')}>
                {spanish ? 'Empezar' : 'Start'}
              </button>
            ) : null}
            <button type="button" className="dashboard-btn" disabled={busy || waiting || status === 'done'} onClick={() => onMark(step.id, 'done')}>
              {spanish ? 'Hecho' : 'Done'}
            </button>
          </div>
        ) : (
          <span className="razz-plan-chip">{included ? (planName || 'CompliCore') : step.capability}</span>
        )}
      </li>
    );
  };
  return (
    <div className="razz-workflow" aria-label={spanish ? 'Flujo de Razzmatazz' : 'Razzmatazz workflow'}>
      <h3>{spanish ? 'Flujo' : 'Workflow'}</h3>
      {(['CompliCore', 'CompliAdvantage', 'CompliComplete'] as RazzmatazzCapability[]).map((capability) => {
        const copy = capabilityCopy(capability, spanish);
        return (
          <section key={capability} className="razz-capability">
            <h4 className="help-badge-label-row">
              {copy.heading}
              <HelpBadge label={spanish ? `Qué incluye ${copy.heading}` : `What ${copy.heading} includes`}>
                <p>{copy.help}</p>
              </HelpBadge>
            </h4>
            <ol>{stepsForCapability(capability).map(renderStep)}</ol>
          </section>
        );
      })}
    </div>
  );
};

export default RazzmatazzWorkspace;
